import { fetchTestSeriesDetails } from '@api/fetchers/test-series';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import TestSeriesListingRevamp from '@cont/SeeAll/TestSeriesListing/TestSeriesRevamp/TestSeriesListing';
import Layout from '@layout/index';
import { SWRConfig } from 'swr';
import { getTestSeriesDetailsAPIKey } from '@cont/SeeAll/utils';
import { useRouter } from 'next/router';
import useTestSeriesInfo from '@cont/SeeAll/TestSeriesListing/TestSeriesRevamp/useTestSeriesInfo';
import { emptyObject } from '@constants/empty';
import courseSchema from '@seo/course/course';
import SEO from '@seo/index';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';

const TestSeries = ({ fallbackData }) => {
  const { query } = useRouter();
  const testSeriesInfokey = getTestSeriesDetailsAPIKey(query.courseUID);

  const { data } = useTestSeriesInfo({
    fallbackData: fallbackData.testSeriesData
  });

  const {
    uid,
    isPublic,
    name = '',
    description,
    slug,
    thumbnail,
    coverPhoto,
    ogImage,
    language,
    goal = emptyObject
  } = data;

  const canonicalURL =
    slug && uid ? `https://unacademy.com/test-series/${slug}/${uid}` : null;
  const metaTitle = `${goal.name || 'Test series'} - ${name} on Unacademy`;
  const metaDescription = `${goal.name || 'Test series'} - ${name} available on
 Unacademy is a topic-wise test series in ${language} covering Multiple Choice Questions from high-
 weightage topics.`;

  const schema = uid && isPublic ? courseSchema(goal, description, data) : null;

  return (
    <>
      <SWRConfig
        value={{
          revalidateOnMount: false,
          dedupingInterval: 60 * 1000,
          fallback: {
            [testSeriesInfokey]: fallbackData.testSeriesData
          }
        }}
      >
        <SEO
          title={metaTitle}
          url={canonicalURL}
          description={metaDescription}
          openGraph={{
            title: metaTitle,
            description: metaDescription,
            images: [
              {
                url: ogImage || thumbnail || coverPhoto
              }
            ],
            canonicalURL
          }}
          schema={schema}
        />
        <TestSeriesListingRevamp fromStore />
        <OneLinkSmartScript />
      </SWRConfig>
    </>
  );
};

export const getStaticProps = async ({ params: query }) => {
  try {
    const testSeriesData = await fetchTestSeriesDetails(query.courseUID);

    if (testSeriesData.error) {
      return { notFound: true, revalidate: 60 };
    }

    return {
      props: {
        fallbackData: {
          testSeriesData
        }
      },
      revalidate: 5 * 60
    };
  } catch {
    return { notFound: true, revalidate: 60 };
  }
};

TestSeries.pageInfo = {
  name: 'Test',
  isMWebSupported: true,
  layoutOptions: {
    useLightBaseColor: true
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default TestSeries;

TestSeries.Layout = Layout;
TestSeries.Wrapper = PageReduxProvider;
